// Data-driven configuration for the finance-search experience.
// Flip REACT_APP_SEARCH_MODE=off to restore the legacy modal-first hero without code changes.
export const SEARCH_MODE = (process.env.REACT_APP_SEARCH_MODE || "").trim().toLowerCase() === "on";

export const HERO_COPY = {
  h1: "Find the finance that fits.",
  sub: "Finance search, powered by human intelligence.",
  brandLine: "Technology finds the pathways. Human intelligence makes sense of them.",
  proposition: "Search funding pathways across banks, non-banks and private capital.",
  label: "What are you looking to fund?",
  placeholder: "e.g. $2m bridging loan in Melbourne",
  cta: "Search Finance",
  secondary: "Speak with a specialist",
};

// Restrained homepage hero suggestions (4 only — keeps the hero minimal)
export const HERO_SUGGESTIONS = [
  { label: "Bridging Finance", query: "bridging finance" },
  { label: "Refinance", query: "refinance my loan" },
  { label: "Business Finance", query: "business loan" },
  { label: "Private Credit", query: "private credit loan" },
];

// Suggested search chips — label shown to user, query sent to /search
export const SEARCH_CHIPS = [
  { label: "Home Loan", query: "home loan" },
  { label: "Refinance", query: "refinance my loan" },
  { label: "Business Loan", query: "business loan" },
  { label: "Bridging Finance", query: "bridging finance" },
  { label: "Private Credit", query: "private credit loan" },
  { label: "Construction", query: "construction and development finance" },
  { label: "Commercial Property", query: "commercial property loan" },
  { label: "SMSF", query: "SMSF property loan" },
  { label: "Asset Finance", query: "asset and equipment finance" },
];

// Homepage vertical tiles → protected /finance/* hubs (crawlable links)
export const HOME_VERTICALS = {
  primary: [
    { title: "Commercial Mortgages", slug: "commercial-property-loans", desc: "Purchase, refinance and equity release across retail, office, industrial and medical property Australia-wide.", cta: "Explore commercial mortgages", testid: "loan-type-commercial-mortgages" },
    { title: "Development Funding", slug: "construction-finance", desc: "Land, construction, residual stock and mezzanine finance for property developers — DA-approved through to practical completion.", cta: "Explore development funding", testid: "loan-type-development-funding" },
    { title: "Bridging & Private Mortgages", slug: "bridging-finance", desc: "Short-term bridging, second-mortgage, caveat and private lender solutions when the banks can't move fast enough.", cta: "Explore bridging finance", testid: "loan-type-bridging-private-mortgages" },
  ],
  secondary: [
    { title: "Private Credit", slug: "private-credit", desc: "Funding outside traditional bank channels, often secured by commercial property or other business assets.", testid: "loan-type-private-credit" },
    { title: "Private Lender Exit", slug: "private-lender-exit", desc: "Take-out finance for short-term bridging loans rolling toward maturity.", testid: "loan-type-private-lender-exit" },
    { title: "Business & Cash-flow", slug: "business-loans", desc: "Working capital, invoice, trade and unsecured business finance.", testid: "loan-type-business---cash-flow" },
    { title: "SMSF & Asset Finance", slug: "smsf-property-loans", desc: "Property loans inside SMSF and equipment / vehicle finance for operators.", testid: "loan-type-smsf---asset-finance" },
  ],
  more: [
    { title: "Refinance", slug: "refinance" },
    { title: "Low-doc loans", slug: "low-doc-loans" },
    { title: "Working capital", slug: "working-capital-finance" },
    { title: "Equipment finance", slug: "equipment-finance" },
  ],
};

export const searchUrl = (q) => `/search?q=${encodeURIComponent((q || "").trim())}`;

// Human-powered finance search — journey copy shared by homepage + results
// BRAND DEVICE: "Hi behind Ai" — casing is deliberate (Hi = Human intelligence, Ai = Artificial intelligence). Never uppercase it.
export const HI_BEHIND_AI = "Hi behind Ai";
export const HI_BEHIND_AI_EXPANSION = "Human intelligence behind Artificial intelligence.";

export const HUMAN_LAYER = {
  eyebrow: "Human-powered finance search",
  heading: "The human layer behind your finance search.",
  body: "OZY organises the essentials of your finance search. Experienced finance professionals review your requirements and objectives, investigate relevant funding pathways and discuss the options worth considering with you.",
  control: "We discuss the options with you. You decide what you want to pursue.",
  compare: [
    { who: "Search engines", what: "Give you information." },
    { who: "Comparison sites", what: "Give you product lists." },
    { who: "OzyLoans", what: "Puts human intelligence behind your finance search.", accent: true },
  ],
};

export const JOURNEY_STEPS = [
  { n: "01", t: "Search", d: "Describe what you're looking to fund in plain English." },
  { n: "02", t: "OZY organises", d: "OZY structures the essentials — amount, finance type, purpose, location, security, timing and relevant details." },
  { n: "03", t: "Human review", d: "An experienced OzyLoans finance professional reviews your requirements and objectives and investigates relevant funding pathways." },
  { n: "04", t: "Choose & connect", d: "We discuss relevant options with you. You decide what you want to pursue. OzyLoans then assists with the institution and application process." },
];

export const HANDOFF = {
  eyebrow: "Human review",
  heading: "Now let a human review it.",
  body: "Our finance team can review your requirements and objectives, consider the available funding pathways and discuss the options that may be worth pursuing.",
  indicative: "Indicative pathways for human review — not lender recommendations.",
  primary: "Get My Human Review",
  secondary: "Speak with a specialist",
  tertiary: "Refine the search with OZY",
  intent: "human_review",
};

// Historical preview records used the earlier intent value; both mean the same hand-off.
export const HUMAN_REVIEW_INTENTS = ["human_review", "human_assessment"];

// Funding universe — discovery categories only (not promises of qualification or product availability).
// Each entry routes into the existing search/discovery architecture; `to` overrides with a protected page.
export const FUNDING_UNIVERSE = [
  { key: "banks", label: "BANKS.", tone: "text-[#E50914] font-bold", query: "bank commercial finance" },
  { key: "non_banks", label: "NON-BANKS.", tone: "text-white font-bold", query: "non-bank finance" },
  { key: "private_capital", label: "PRIVATE CAPITAL.", tone: "text-[#E50914] font-bold", to: "/finance/private-credit" },
  { key: "low_doc", label: "LOW DOC.", tone: "text-zinc-400 font-bold", query: "low doc finance" },
  { key: "alt_doc", label: "ALT DOC.", tone: "text-white font-bold", query: "alt doc finance" },
  { key: "no_doc", label: "NO DOC.", tone: "text-[#E50914] font-bold", query: "no doc finance" },
];

export const universeUrl = (u) => u.to || `/search?q=${encodeURIComponent(u.query)}&universe=${u.key}`;

export const POPUP = {
  eyebrow: "From OzyLoans",
  title: "Let a human review your finance search.",
  body: "Our finance professionals review your requirements and objectives and discuss the options worth pursuing with you.",
  cta: "Get my human review",
};
